import { SelectField, TextAreaField, TextField } from "../../ui/fields";
import type { CommonKey, CommonValues } from "../form-mappers";
import { fulfilmentOptions } from "../labels";
import type { FieldErrors } from "../validation";
import { fieldGrid } from "../components/styles";

// The common fields of every catalog item, as a controlled set of controls. The create page and the
// edit section both render it; neither adds a conditional per item type here (the type's own fields are
// a separate component, chosen by the registry in `subtypes/registry.ts`).

export const commonFieldLabels: Readonly<Record<CommonKey, string>> = {
  name: "Name",
  slug: "Slug",
  shortDescription: "Short description",
  description: "Description",
  addressText: "Address",
  locality: "Locality",
  region: "Region",
  countryCode: "Country code",
  latitude: "Latitude",
  longitude: "Longitude",
  timeZone: "Time zone",
  fulfilmentMode: "Fulfilment mode",
  externalUrl: "External URL",
  editorialPriority: "Editorial priority",
};

export const commonFieldOrder: readonly CommonKey[] = [
  "name",
  "slug",
  "shortDescription",
  "description",
  "addressText",
  "locality",
  "region",
  "countryCode",
  "latitude",
  "longitude",
  "timeZone",
  "fulfilmentMode",
  "externalUrl",
  "editorialPriority",
];

type CommonFieldsProps = Readonly<{
  idPrefix: string;
  values: CommonValues;
  errors: FieldErrors;
  onChange: (key: CommonKey, value: string) => void;
  disabled: boolean;
  /** The slug cannot change after the first publish (it is part of a link customers may have). */
  slugLocked?: boolean;
  /** A payment-grade price needs the human concierge fulfilment mode. */
  fulfilmentLocked?: boolean;
  /** On create, the fulfilment mode may stay unset: the server picks its default. */
  allowBlankFulfilment?: boolean;
}>;

export function CommonFields({
  idPrefix,
  values,
  errors,
  onChange,
  disabled,
  slugLocked = false,
  fulfilmentLocked = false,
  allowBlankFulfilment = false,
}: CommonFieldsProps) {
  const id = (key: CommonKey) => `${idPrefix}-${key}`;
  const text = (
    key: CommonKey,
    extra: {
      hint?: string;
      maxLength?: number;
      required?: boolean;
      readOnly?: boolean;
      inputMode?: "decimal" | "numeric";
      type?: "number" | "url";
    } = {},
  ) => (
    <TextField
      id={id(key)}
      label={commonFieldLabels[key]}
      value={values[key]}
      onChange={(value) => onChange(key, value)}
      error={errors[key]}
      disabled={disabled}
      {...extra}
    />
  );

  return (
    <div className={fieldGrid}>
      {text("name", { required: true, maxLength: 200 })}
      {text("slug", {
        required: true,
        maxLength: 120,
        readOnly: slugLocked,
        hint: slugLocked
          ? "The slug cannot change after the first publish."
          : "Lowercase letters, numbers and single hyphens, for example casa-azul.",
      })}
      <div className="col-span-full">
        <TextAreaField
          id={id("shortDescription")}
          label={commonFieldLabels.shortDescription}
          value={values.shortDescription}
          onChange={(value) => onChange("shortDescription", value)}
          error={errors.shortDescription}
          disabled={disabled}
          rows={2}
          maxLength={300}
          hint="One or two sentences. Up to 300 characters."
        />
      </div>
      <div className="col-span-full">
        <TextAreaField
          id={id("description")}
          label={commonFieldLabels.description}
          value={values.description}
          onChange={(value) => onChange("description", value)}
          error={errors.description}
          disabled={disabled}
          rows={7}
          maxLength={8000}
        />
      </div>
      {text("addressText", { maxLength: 300 })}
      {text("locality", { maxLength: 100 })}
      {text("region", { maxLength: 100 })}
      {text("countryCode", { maxLength: 2, hint: "Two letters, for example ES." })}
      {text("latitude", { inputMode: "decimal", hint: "Between -90 and 90." })}
      {text("longitude", { inputMode: "decimal", hint: "Between -180 and 180." })}
      {text("timeZone", { maxLength: 64, hint: "An IANA name, for example Europe/Madrid." })}
      <SelectField
        id={id("fulfilmentMode")}
        label={commonFieldLabels.fulfilmentMode}
        value={values.fulfilmentMode}
        onChange={(value) => onChange("fulfilmentMode", value)}
        options={fulfilmentOptions}
        {...(allowBlankFulfilment ? { placeholder: "Use the default" } : {})}
        error={errors.fulfilmentMode}
        disabled={disabled || fulfilmentLocked}
        hint={
          fulfilmentLocked
            ? "This item has a payment-grade price, so it must stay with the human concierge."
            : undefined
        }
      />
      {text("externalUrl", {
        type: "url",
        maxLength: 2048,
        hint: "An https address customers may be sent to.",
      })}
      {text("editorialPriority", {
        type: "number",
        inputMode: "numeric",
        hint: "0 means not featured. A higher number is shown first.",
      })}
    </div>
  );
}
